/**
 * Pack economics (FRIDGE-726) — the ONE place a filter listing's pack
 * quantity and price-per-filter come from.
 *
 * A pack is NOT a new product identity: the FFN identifies the filter, and a
 * 3-pack is an OFFER SHAPE of it. No feed we ingest carries a pack quantity
 * (retailer_offers.price_per_unit_display is NULL on every filter offer,
 * measured 09/04/2026), so the quantity is READ from the listing title —
 * "3-Pack", "Pack of 3", "(3 Pack)", "3 Pcs", "Single-Pack", "Twin Pack" —
 * and the per-filter price is COMPUTED from the listing price. Nothing is
 * asserted: a title that names no quantity is one filter, and a listing with
 * no live price has no per-filter price.
 *
 * Every filter surface renders through this module (the PDP offer rows, the
 * /filters catalog cards, the per-model hub cards and chips, the brand pages,
 * the finder) so the arithmetic and the wording can never disagree.
 */
import { familyForType } from '@/lib/parts/fpn'
import { formatCents } from '@/lib/format-utils'

/** Largest pack a refrigerator-filter listing plausibly sells; above it the number is a code, not a count. */
export const MAX_PACK_QUANTITY = 24

type Matcher = { re: RegExp; quantity: (m: RegExpMatchArray) => number }

// Order matters: "1 Pack of 2" means one pack holding two, so "pack of N"
// resolves before "N pack". Aside from the verified anchored listing prefix,
// patterns need a pack WORD next to the number — "everydrop Filter 3", "6 Month", "200 Gallon", "NSF 42" never
// match, because a bare number is never a quantity.
const MATCHERS: Matcher[] = [
  // Verified listing notation: "1X Waterdrop", "2X Waterdrop", "3X Waterdrop".
  // Anchor to that supplier prefix; do not reinterpret dimensions, model codes
  // or generic "3x filtration" marketing as a quantity.
  { re: /^\s*(\d{1,2})[x×]\s+(?=Waterdrop\b)/i, quantity: (m) => Number(m[1]) },
  { re: /\b(?:pack|set|box|case)\s+of\s+(\d{1,2})\b/i, quantity: (m) => Number(m[1]) },
  { re: /\b(\d{1,2})\s*[-–]?\s*(?:packs?|pk|pcs?|pieces?|count|ct)\b/i, quantity: (m) => Number(m[1]) },
  { re: /\((\d{1,2})\)\s*[.…]?\s*$/, quantity: (m) => Number(m[1]) },
  { re: /\b(\d{1,2})\s+filters\b/i, quantity: (m) => Number(m[1]) },
  { re: /\b(?:twin|double|dual)[- ]?packs?\b/i, quantity: () => 2 },
  { re: /\btriple[- ]?packs?\b/i, quantity: () => 3 },
  { re: /\bsingle[- ]?packs?\b/i, quantity: () => 1 },
]

/**
 * The pack quantity a listing title names, or null when it names none.
 * Never below 1, never above MAX_PACK_QUANTITY.
 */
export function parsePackQuantity(title: string | null | undefined): number | null {
  if (typeof title !== 'string' || !title.trim()) return null
  for (const { re, quantity } of MATCHERS) {
    const m = title.match(re)
    if (!m) continue
    const q = quantity(m)
    if (!Number.isInteger(q) || q < 1 || q > MAX_PACK_QUANTITY) continue
    return q
  }
  return null
}

/** The quantity a listing sells — the title's number, else one filter. */
export function packQuantityOf(title: string | null | undefined): number {
  return parsePackQuantity(title) ?? 1
}

/**
 * Price of ONE filter inside a listing priced at `packCents` for `quantity`
 * filters, rounded to the cent. Null when there is no live price.
 */
export function perFilterCents(packCents: number | null | undefined, quantity: number): number | null {
  if (typeof packCents !== 'number' || !Number.isFinite(packCents) || packCents <= 0) return null
  const q = Number.isInteger(quantity) && quantity >= 1 ? quantity : 1
  return Math.round(packCents / q)
}

export interface PackEconomics {
  /** Filters in the listing (1 when the title names none). */
  quantity: number
  /** True when the listing is a multi-pack. */
  isPack: boolean
  /** The listing's live price, or null. */
  packCents: number | null
  /** The computed price of one filter, or null when unpriced. */
  perFilterCents: number | null
}

/** Quantity + per-filter price for a listing, from its title and live price. */
export function packEconomics(title: string | null | undefined, packCents: number | null | undefined): PackEconomics {
  const quantity = packQuantityOf(title)
  const cents = typeof packCents === 'number' && packCents > 0 ? packCents : null
  return { quantity, isPack: quantity > 1, packCents: cents, perFilterCents: perFilterCents(cents, quantity) }
}

/** "3-pack" — the label a multi-pack carries next to its pack price. */
export function packLabel(quantity: number): string {
  return `${quantity}-pack`
}

/**
 * The per-unit line the PDP offer rows show for a FILTER listing sold as a
 * pack — "$15.79 per filter · 3-pack" — or null for a single, an appliance,
 * a part, or an unpriced offer. Feeds the same PricePerUnit slot the
 * supplier's own price_per_unit_display would (it never overrides one).
 */
export function filterPerUnitDisplay(product: { type?: string | null; title?: string | null }, offerCents: number | null | undefined): string | null {
  if (familyForType(product.type ?? null) !== 'filters') return null
  const eco = packEconomics(product.title, offerCents)
  if (!eco.isPack || eco.perFilterCents === null) return null
  return `${formatCents(eco.perFilterCents)} per filter · ${packLabel(eco.quantity)}`
}
